import { Attractions, Bed, DirectionsBoat, DirectionsBus, Flight } from "@mui/icons-material";
import { Box, Grid, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import {
  AdvancedMarker,
  ControlPosition,
  InfoWindow,
  Map,
  MapControl,
  useApiIsLoaded,
  useMap,
} from "@vis.gl/react-google-maps";
import type { ReactElement, ReactNode } from "react";
import { Children, cloneElement, isValidElement, useEffect, useMemo, useState } from "react";
import DatePicker from "../../../components/datePicker/DatePicker";
import dayjs, { ISO_DATE_FORMAT } from "../../../dayjs";
import { providerColor } from "../../providers/providerColor";
import type { ProviderProductType } from "../../providers/providersApi";
import type { ProductResponse } from "../productsApi";
import { usePricesOnDate } from "../useProducts";
import { boundsContainingAll } from "./boundsContainingAll";
import { MapProductDetails } from "./MapProductDetails";
import { PriceTag } from "./PriceTag";
import { productWithoutLocation } from "./productWithoutLocation";
import { ProviderMultiSelect } from "./ProviderMultiSelect";
import type { ProviderTypeSelectValue } from "./ProviderTypeSelect";
import { asProviderProductType, ProviderTypeSelect } from "./ProviderTypeSelect";

const stockholm = {
  lat: 59.334591,
  lng: 18.06324,
};

export function ProductMapForProducts({
  children,
  products,
  date: initialDate = dateToIsoDate(new Date()),
}: {
  children?: ReactElement<{ product: ProductResponse; date?: string }>;
  products: ProductResponse[];
  date?: string;
}) {
  const [providerIds, setProviderIds] = useState<string[]>([]);
  const [providerTypes, setProviderTypes] = useState<ProviderTypeSelectValue[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<ProductResponse | null>(null);
  const [date, setDate] = useState(initialDate);
  const prices = usePricesOnDate(date);
  const mapsApiLoaded = useApiIsLoaded();
  const map = useMap();
  const providerProductTypes = useMemo(() => asProviderProductType(providerTypes), [providerTypes]);

  const located = useMemo(
    () =>
      products
        .filter((p) => p.contact.googlePlaceGeometry)
        .filter((p) => providerProductTypes.includes(p.provider.productType!))
        .filter((p) => providerIds.length === 0 || providerIds.includes(p.provider.id)),
    [products, providerIds, providerProductTypes],
  );

  useEffect(() => {
    if (!map || !mapsApiLoaded || !located) return;
    const bounds = boundsContainingAll(located.map((l) => l.contact.googlePlaceGeometry!));
    if (bounds) {
      map.fitBounds(bounds);
    }
  }, [mapsApiLoaded, map, located]);

  const childrenWithProps = Children.map(children, (child) => {
    if (isValidElement(child)) {
      return cloneElement(child, { product: selectedLocation ?? undefined, date });
    }
    return child;
  });

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        pt: { xs: "4em", md: "8em" },
      }}
    >
      <Map
        mapId="ProductMap"
        style={{ width: "100%", height: "100%" }}
        defaultCenter={stockholm}
        defaultZoom={5}
        gestureHandling={"greedy"}
        disableDefaultUI={true}
        mapTypeControl={true}
        mapTypeControlOptions={{ position: ControlPosition.TOP_RIGHT }}
        zoomControl={true}
        onClick={() => {
          setSelectedLocation(null);
        }}
      >
        <MapControl position={ControlPosition.TOP_LEFT}>
          <div
            style={{
              background: "white",
              padding: 12,
              width: 300,
              display: "flex",
              gap: 16,
              flexDirection: "column",
              opacity: 0.9,
            }}
          >
            <DatePicker label="Data dla której wyświetlić cenę:" fullWidth clearable value={date} onChange={setDate} />
            <ProviderMultiSelect providerIds={providerIds} onChange={setProviderIds} />
            <ProviderTypeSelect values={providerTypes} onChange={setProviderTypes} />
          </div>
        </MapControl>
        {located?.map((product) => (
          <AdvancedMarker
            key={product.id}
            position={product.contact.googlePlaceGeometry!.location}
            onClick={() => setSelectedLocation(product)}
          >
            <PriceTag {...providerColor(product.provider)}>
              {prices.isLoading ? (
                <CircularProgress size={20} />
              ) : (
                (prices.prices[product.id] ?? iconForProviderType[product.provider.productType!])
              )}
            </PriceTag>
          </AdvancedMarker>
        ))}

        {selectedLocation && (
          <InfoWindow
            position={selectedLocation.contact.googlePlaceGeometry!.location}
            onCloseClick={() => setSelectedLocation(null)}
            disableAutoPan={false}
            pixelOffset={[0, -35]}
            headerContent={<Typography variant="subtitle2">{selectedLocation.name}</Typography>}
          >
            <MapProductDetails product={productWithoutLocation(selectedLocation)}>
              {children && <Grid size={12}>{childrenWithProps}</Grid>}
            </MapProductDetails>
          </InfoWindow>
        )}
      </Map>
    </Box>
  );
}

function dateToIsoDate(dateObj: Date): string {
  return dayjs(dateObj).format(ISO_DATE_FORMAT);
}

const iconForProviderType: Record<ProviderProductType, ReactNode> = {
  ATTRACTION: <Attractions fontSize="small" />,
  HOTEL: <Bed fontSize="small" />,
  HOTEL_VAT_NP: <Bed fontSize="small" />,
  PLANE_TICKET_VAT_0: <Flight fontSize="small" />,
  PLANE_TICKET_VAT_NP: <Flight fontSize="small" />,
  BUS_TRANSPORT_VAT_NP: <DirectionsBus fontSize="small" />,
  BUS_TRANSPORT_VAT_8: <DirectionsBus fontSize="small" />,
  WATERBORNE_TRANSPORT_VAT_0: <DirectionsBoat fontSize="small" />,
  WATERBORNE_TRANSPORT_VAT_NP: <DirectionsBoat fontSize="small" />,
};
